import { Box, Typography, TextField, Switch, Button } from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import {
	BudgetLimitEnforcementMode,
	BudgetConsumption,
} from "@/domain/program.interface";
import BudgetConsumptionBar from "./BudgetConsumptionBar";
import { useEffect, useState } from "react";

interface BudgetEnforcementSectionProps {
	enabled: boolean;
	budgetLimit: number;
	threshold?: number;
	mode?: BudgetLimitEnforcementMode;
	consumption?: BudgetConsumption | null;
	onEnabledChange: (value: boolean) => void;
	onBudgetLimitChange: (value: number) => void;
	onThresholdChange: (value: number) => void;
	onModeChange: (value: BudgetLimitEnforcementMode) => void;
}

const MODES: {
	value: BudgetLimitEnforcementMode;
	label: string;
	sub: string;
	color: "error" | "warning";
}[] = [
	{
		value: "BLOCK",
		label: "Block",
		sub: "Deny new enrollments at threshold",
		color: "error",
	},
	{
		value: "WARN",
		label: "Warn",
		sub: "Show warning; enrollment still succeeds",
		color: "warning",
	},
];

/**
 * SIMS-6067 / SIMS-7605 — Budget enforcement configuration card (Program Maintenance).
 * Recreates the Hand-off "Budget enforcement" card: enable toggle, and when enabled a
 * two-column layout (SIMS-7605 D1) — config fields (budget limit + threshold % +
 * enforcement mode) on the left, and the live consumption indicator + net-earned
 * formula caption (D7) in the right column.
 */
export default function BudgetEnforcementSection({
	enabled,
	budgetLimit,
	threshold,
	mode,
	consumption,
	onEnabledChange,
	onBudgetLimitChange,
	onThresholdChange,
	onModeChange,
}: BudgetEnforcementSectionProps): JSX.Element {
	// SIMS-7605: local buffer for the threshold input so it can be freely cleared/retyped. The
	// previous per-keystroke clamp forced empty -> 1, so the leading "1" could never be removed to
	// type e.g. 50. Typing commits a clamped 1-100 value; the buffer re-syncs when the prop changes.
	const [thresholdInput, setThresholdInput] = useState<string>(
		threshold != null ? String(threshold) : "",
	);
	useEffect(() => {
		setThresholdInput(threshold != null ? String(threshold) : "");
	}, [threshold]);
	return (
		<Box
			sx={{
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 1,
				p: 2,
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "space-between",
					pb: enabled ? 2 : 1,
					borderBottom: "1px solid",
					borderColor: "divider",
				}}
			>
				<Box>
					<Typography variant="subtitle1" fontWeight={600}>
						Budget enforcement
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Gate new enrollments when earned incentives approach the program
						limit.
					</Typography>
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography
						variant="body2"
						color={enabled ? "primary" : "text.secondary"}
					>
						{enabled ? "Enabled" : "Disabled"}
					</Typography>
					<Switch
						checked={enabled}
						onChange={(e) => onEnabledChange(e.target.checked)}
						inputProps={
							{
								"data-testid": "program-budget-limit-enabled-toggle",
								"aria-label": "Enable Budget Limit",
							} as React.InputHTMLAttributes<HTMLInputElement>
						}
					/>
				</Box>
			</Box>

			{!enabled && (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						color: "text.secondary",
						mt: 1.5,
					}}
				>
					<DoNotDisturbOnIcon fontSize="small" />
					<Typography variant="body2">
						Enforcement is off — enrollments are never gated by budget.
					</Typography>
				</Box>
			)}

			{enabled && (
				<Box
					sx={{
						mt: 2.5,
						display: "flex",
						flexWrap: "wrap",
						alignItems: "flex-start",
						gap: 3,
					}}
				>
					<Box
						data-testid="program-budget-enforcement-config"
						sx={{
							flex: "1 1 280px",
							display: "flex",
							flexDirection: "column",
							gap: 2.5,
						}}
					>
						<TextField
							fullWidth
							required
							label="Budget limit (USD)"
							type="number"
							value={Number.isFinite(budgetLimit) ? budgetLimit : ""}
							onChange={(e) =>
								onBudgetLimitChange(parseInt(e.target.value, 10))
							}
							helperText="Total dollar cap for net earned incentives across all enrollments."
							inputProps={{
								"data-testid": "program-budget-limit-input",
								min: 0,
							}}
						/>

						<TextField
							fullWidth
							required
							label="Threshold (%)"
							type="number"
							value={thresholdInput}
							onChange={(e) => {
								const raw = e.target.value;
								setThresholdInput(raw); // allow free typing, including clearing
								if (raw === "") return; // don't force a value while empty
								const n = parseInt(raw, 10);
								if (!Number.isNaN(n)) {
									onThresholdChange(Math.max(1, Math.min(100, n)));
								}
							}}
							onBlur={() => {
								const n = parseInt(thresholdInput, 10);
								if (Number.isNaN(n)) {
									// left empty/invalid -> revert to the last committed value
									setThresholdInput(threshold != null ? String(threshold) : "");
									return;
								}
								const clamped = Math.max(1, Math.min(100, n));
								setThresholdInput(String(clamped));
								onThresholdChange(clamped);
							}}
							helperText="Percentage of the budget limit at which enforcement triggers."
							inputProps={{
								"data-testid": "program-budget-limit-threshold-input",
								min: 1,
								max: 100,
							}}
						/>

						<Box>
							<Typography
								variant="caption"
								sx={{ display: "block", mb: 1, fontWeight: 500 }}
							>
								Enforcement mode *
							</Typography>
							<Box sx={{ display: "flex", gap: 1.5 }}>
								{MODES.map((m) => {
									const active = mode === m.value;
									return (
										<Button
											key={m.value}
											fullWidth
											onClick={() => onModeChange(m.value)}
											variant={active ? "contained" : "outlined"}
											color={m.color}
											startIcon={
												m.value === "BLOCK" ? (
													<BlockIcon />
												) : (
													<WarningAmberIcon />
												)
											}
											sx={{
												flexDirection: "column",
												alignItems: "flex-start",
												textTransform: "none",
												py: 1,
											}}
											data-testid={
												m.value === "BLOCK"
													? "program-budget-limit-mode-block"
													: "program-budget-limit-mode-warn"
											}
											aria-pressed={active}
										>
											<Typography variant="body2" fontWeight={600}>
												{m.label}
											</Typography>
											<Typography variant="caption">{m.sub}</Typography>
										</Button>
									);
								})}
							</Box>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ display: "block", mt: 0.75 }}
							>
								{mode === "WARN"
									? "Returns a budgetWarning flag in the enrollment response. Clinician may proceed."
									: "Returns 422 BUDGET_LIMIT_REACHED. Active enrollments are unaffected."}
							</Typography>
						</Box>
					</Box>

					<Box
						data-testid="program-budget-enforcement-right"
						sx={{ flex: "1 1 260px" }}
					>
						{consumption && (
							<>
								<BudgetConsumptionBar
									consumption={{
										...consumption,
										// SIMS-7605: track the live config budget limit so the indicator
										// recomputes immediately as the admin edits it (fall back to the
										// consumption limit only while the input is empty/invalid).
										budgetLimit:
											Number.isFinite(budgetLimit) && budgetLimit > 0
												? budgetLimit
												: consumption.budgetLimit,
									}}
									threshold={threshold}
									mode={mode}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 1.25 }}
								>
									Net earned incentives across all enrollments (active,
									completed, and disenrolled), calculated as EARNED +
									EARNED_BANKED − REVERT.
								</Typography>
							</>
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
}
